import type { Dayjs } from 'dayjs'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import type { FulfillmentType as FulfillmentTypeValue } from '@/lib/services/locations/locations.types'
import { cn } from '@/lib/utils'
import { AlertTriangle, Calendar } from 'lucide-react'

type ScheduledFulfillmentWellProps = {
  fulfillmentType: FulfillmentTypeValue | null
  /** `YYYY-MM-DD`, or a timestamp whose date part is the scheduled day. */
  fulfillmentDate: string | null
  /** The order's due-date presentation; a destructive one says the date has passed. */
  fulfillmentPresentation?: { variant: string; label: string } | null
  isCancelled?: boolean
  compactWellClassName?: string
  className?: string
}

function getDateOnly(value: string | null | undefined): string | null {
  const rawValue = String(value ?? '').trim()

  if (!rawValue) {
    return null
  }

  return rawValue.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || rawValue
}

function parseCalendarDay(value: string | null | undefined): Dayjs | null {
  const dateOnly = getDateOnly(value)

  if (!dateOnly) {
    return null
  }

  try {
    return dayjs(dateOnly)
  } catch {
    return null
  }
}

export function formatScheduledFulfillmentDate(value: string | null | undefined): string {
  const dateOnly = getDateOnly(value)

  if (!dateOnly) {
    return ''
  }

  const date = dayjs.utc(dateOnly)

  return date.isValid() ? date.format('ddd, MMM D') : ''
}

export function formatRelativeCalendarDate(
  value: string | null | undefined,
  today: Dayjs = dayjs()
): string | null {
  const date = parseCalendarDay(value)

  if (!date?.isValid()) {
    return null
  }

  const days = date.startOf('day').diff(today.startOf('day'), 'day')

  if (Math.abs(days) > 30) {
    return null
  }

  if (days === 0) {
    return '(today)'
  }

  if (days === 1) {
    return '(tomorrow)'
  }

  if (days === -1) {
    return '(yesterday)'
  }

  const dayCount = Math.abs(days)

  return days > 1 ? `(in ${dayCount} days)` : `(${dayCount} days ago)`
}

function getScheduledFulfillmentLabel(
  fulfillmentType: FulfillmentTypeValue | null,
  isCancelled = false
): string | null {
  const scheduleVerb = isCancelled ? 'was scheduled for' : 'scheduled for'

  if (fulfillmentType === FulfillmentType.Delivery.Value) {
    return `Delivery ${scheduleVerb}`
  }

  if (fulfillmentType === FulfillmentType.Pickup.Value) {
    return `Pickup ${scheduleVerb}`
  }

  if (fulfillmentType === FulfillmentType.InStore.Value) {
    return `In-store pickup ${scheduleVerb}`
  }

  if (fulfillmentType === FulfillmentType.Shipping.Value) {
    return `Shipping ${scheduleVerb}`
  }

  return null
}

export default function ScheduledFulfillmentWell({
  fulfillmentType,
  fulfillmentDate,
  fulfillmentPresentation,
  isCancelled = false,
  compactWellClassName,
  className,
}: ScheduledFulfillmentWellProps) {
  const label = getScheduledFulfillmentLabel(fulfillmentType, isCancelled)
  const dateLabel = formatScheduledFulfillmentDate(fulfillmentDate)
  const relativeDateLabel = formatRelativeCalendarDate(fulfillmentDate)
  const showFulfillmentDatePassedSignal = fulfillmentPresentation?.variant === 'destructive'
  const displayDateLabel = showFulfillmentDatePassedSignal
    ? fulfillmentPresentation.label
    : dateLabel

  if (!label || !displayDateLabel) {
    return null
  }

  return (
    <div
      className={cn(
        compactWellClassName,
        showFulfillmentDatePassedSignal &&
          'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground ring-1 ring-status-destructive-border',
        className
      )}
      data-testid="scheduled-fulfillment-date-card"
      data-tone={showFulfillmentDatePassedSignal ? 'destructive' : 'neutral'}
    >
      <Typography
        variant="eyebrow"
        tone={showFulfillmentDatePassedSignal ? 'destructive' : undefined}
      >
        {label}
      </Typography>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {showFulfillmentDatePassedSignal ? (
            <AlertTriangle className="size-4 shrink-0 text-destructive" />
          ) : (
            <Calendar className="size-4 shrink-0 text-muted-foreground" />
          )}
          <Typography
            variant="h5"
            tone={showFulfillmentDatePassedSignal ? 'destructive' : 'default'}
            className="min-w-0 pb-0"
          >
            {displayDateLabel}
          </Typography>
          {!isCancelled && !showFulfillmentDatePassedSignal && relativeDateLabel ? (
            <Typography variant="supporting">{relativeDateLabel}</Typography>
          ) : null}
        </div>
        {showFulfillmentDatePassedSignal ? (
          <Badge variant="destructive" size="xs">
            {fulfillmentType === FulfillmentType.Pickup.Value
              ? 'Pickup date passed'
              : 'Delivery date passed'}
          </Badge>
        ) : null}
      </div>
    </div>
  )
}
